import { z } from 'zod';

export const NumberSchema = z
  .number()
  .refine(n => !Number.isNaN(n) && Number.isFinite(n), {
    message: 'Number must be a finite value'
  });

export const NumberBooleanSchema = z.number().transform(v => {
  if (v !== 0 && v !== 1) {
    throw new Error('Boolean number must be 0 or 1');
  }
  return v === 1;
});

// A Range is represented by 2 floats, a and b.
export type Range = [number, number];

export const NUMERIC_EPSILON = 1e-6;

/**
 * Returns the minimum value of the range.
 */
export function rangeMin(range: Range): number {
  return range[0];
}

/**
 * Returns the maximum value of the range.
 */
export function rangeMax(range: Range): number {
  return range[1];
}

/**
 * Performs a linear interpolation in the range.
 *
 * If `t` is outside the range [0, 1], the result is extrapolated.
 *
 * @param t The interpolation parameter.
 * @param range The range to interpolate in.
 * @returns The interpolated number.
 */
export function lerp(t: number, range: Range): number {
  return range[0] + (range[1] - range[0]) * t;
}

export interface Lerpable {
  lerp(t: number, end: this): this;
}

/**
 * Returns a function that performs a linear interpolation in the range.
 *
 * If `t` is outside the range [0, 1], the result is extrapolated.
 *
 * @param range The range to interpolate in.
 * @returns The interpolation function.
 */
export function interp(range: Range): (t: number) => number {
  if (range[0] === range[1]) {
    return () => range[0];
  } else {
    return (t: number) => lerp(t, range);
  }
}

export interface Interpable {
  interp(end: this): (t: number) => this;
}

/**
 * Map the value x from the first range into the second.
 *
 * @param x The value to map.
 * @param from The first range.
 * @param to The second range.
 * @returns The mapped value.
 */
export function lerpMap(x: number, from: Range, to: Range): number {
  return lerp((x - from[0]) / (from[1] - from[0]), to);
}

/**
 * Returns true if the two ranges are equal.
 * @param a The first range.
 * @param b The second range.
 * @returns True if the two ranges are equal.
 */
export function rangesAreEqual(a: Range, b: Range): boolean {
  if (a === b) return true;
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Returns a function that maps the value x from the first range into the second.
 *
 * @param from The first range.
 * @param to The second range.
 * @returns The mapping function.
 */
export function interpMap(from: Range, to: Range): (x: number) => number {
  if (rangesAreEqual(from, to)) {
    return (x: number) => x;
  } else {
    return (x: number) => lerpMap(x, from, to);
  }
}

/**
 * Clamps a value to a range.
 *
 * @param value The value.
 * @param range The range. Defaults to [0, 1].
 * @returns The clamped value.
 */
export function clamp(value: number, range: Range = [0, 1]): number {
  return Math.min(Math.max(value, range[0]), range[1]);
}

/**
 *
 * Find the value in the given range that minimizes the given error function.
 */
export function binarySearch(
  range: Range,
  error: (x: number) => number,
  maxIterations: number,
  tolerance = 1e-6
): number {
  let [a, b] = range;
  let fa = error(a);
  let fb = error(b);
  if (fa === 0) return a;
  if (fb === 0) return b;
  if (fa * fb > 0) {
    throw new Error('Root is not bracketed');
  }
  let iterationsRemaining = maxIterations;
  while (b - a > tolerance && iterationsRemaining-- > 0) {
    const c = (a + b) / 2;
    const fc = error(c);
    if (fc === 0) return c;
    if (fc * fa < 0) {
      b = c;
      fb = fc;
    } else {
      a = c;
      fa = fc;
    }
  }
  return (a + b) / 2;
}

// ```java
// public static double quantize(double value, double bucket) {
//   return Math.floor(((value + (bucket / 2.0)) / bucket)) * bucket;
// }
// ```

/**
 * Quantize a value to the nearest multiple of a bucket.
 *
 * @param value The value to quantize.
 * @param bucket The bucket size.
 * @returns The quantized value.
 */
export function quantize(value: number, bucket: number): number {
  return Math.floor((value + bucket / 2) / bucket) * bucket;
}

/**
 * Determines if two numbers are close to each other within a specified epsilon.
 *
 * @param a - The first number to compare.
 * @param b - The second number to compare.
 * @param epsilon - The maximum allowed difference between the two numbers. Defaults to `NUMERIC_EPSILON`.
 * @returns `true` if the numbers are close to each other within the specified epsilon, otherwise `false`.
 */
export function isClose(
  a: number,
  b: number,
  epsilon: number = NUMERIC_EPSILON
): boolean {
  const d = a - b;
  return -epsilon <= d && d <= epsilon;
}

/**
 * Converts a number to a string with a up to a specified number of decimal places,
 * rounding to avoid unnecessary trailing zeros.
 *
 * Ensures that values close to zero, including floating point `-0`, are
 * formatted as `0`.
 *
 * @param value - The number to be converted.
 * @param fractionDigits - Optional. The maximum number of digits to appear after
 * the decimal point.
 * @returns A string representing the given number rounded to the specified
 * precision, without unnecessary trailing zeros.
 */
export function fixedNumber(value: number, fractionDigits?: number): string {
  if (isClose(value, 0)) {
    return '0';
  }

  const roundedValue =
    fractionDigits !== undefined
      ? parseFloat(value.toFixed(fractionDigits)) // Rounds to specified digits
      : value;

  return roundedValue.toString();
}

/**
 * Converts a floating-point number to a fixed-point notation and returns it as a number.
 *
 * @param value - The floating-point number to be converted.
 * @param fractionDigits - The number of digits to appear after the decimal point. Defaults to 6.
 * @returns The converted number in fixed-point notation.
 */
export function fixedFloat(value: number, fractionDigits = 6): number {
  return parseFloat(value.toFixed(fractionDigits));
}

/**
 * Generates an array of linearly spaced numbers between 0 and 1, inclusive.
 *
 * @param n - The number of elements in the resulting array.
 * @returns An array of `n` linearly spaced numbers between 0 and 1.
 *
 * @example
 * ```typescript
 * linspace(3); // [0, 0.5, 1]
 * ```
 */
export function linspace(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i / (n - 1));
}

/**
 * Converts a number to its hexadecimal string representation.
 *
 * @param n - The number to convert. Must be an integer between 0 and 255.
 * @returns The hexadecimal string representation of the number, padded to 2 characters.
 * @throws {Error} If the number is not an integer between 0 and 255.
 */
export function toHex(n: number): string {
  if (n < 0 || n > 255 || !Number.isInteger(n)) {
    throw new Error('Number must be an integer between 0 and 255');
  }
  return n.toString(16).padStart(2, '0');
}

/**
 * Converts a number between 0 and 1 to its hexadecimal string representation.
 *
 * @param n - The number to convert. Must be a floating-point number between 0 and 1.
 * @returns The hexadecimal string representation of the number, padded to 2 characters.
 * @throws {Error} If the number is not a floating-point number between 0 and 1.
 * @example
 */
export function toHexFloat(n: number): string {
  if (n < 0 || n > 1) {
    throw new Error('Number must be between 0 and 1');
  }
  return toHex(Math.round(n * 255));
}

export function toPercentFloat(n: number, fractionDigits?: number): string {
  return fixedNumber(n * 100, fractionDigits) + '%';
}
